import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/seo/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { SITE_ORIGIN } from "@/lib/leaks";
import { site } from "@/mocks/site";
import ContactForm from "@/pages/contact/components/ContactForm";

const channels = [
  {
    icon: "ri-mail-send-line",
    label: "爆料投稿邮箱",
    value: site.submitEmail,
    note: "线索、画面、补充信息优先走这个入口",
  },
  {
    icon: "ri-customer-service-2-line",
    label: "合作与反馈邮箱",
    value: site.email,
    note: "内容纠错、转载授权与商务合作",
  },
];

const guideSteps = [
  {
    no: "1",
    title: "写清关键信息",
    desc: "时间、地点、发生了什么，越具体越好。附上可核实的补充线索会提高采用率。",
  },
  {
    no: "2",
    title: "说明线索来源",
    desc: "是你亲眼所见，还是转述或记录？如实说明来源，方便编辑判断如何标注可信度。",
  },
  {
    no: "3",
    title: "等待编辑核对",
    desc: "编辑会先核对再决定是否采用。无论结果如何，都会在收到后的工作日内给出回复。",
  },
  {
    no: "4",
    title: "注意隐私边界",
    desc: "请勿提交他人隐私信息或未经核实的负面指控，这类内容我们无法采用。",
  },
];

export default function Contact() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系蝌蚪爆料",
      url: `${SITE_ORIGIN}/contact`,
      description: "向蝌蚪爆料投稿线索、纠正内容或咨询合作，编辑会在工作日内回复。",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: site.name,
      url: SITE_ORIGIN,
      email: site.email,
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: site.submitEmail,
        availableLanguage: ["zh-CN"],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "联系投稿" },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="联系与投稿 | 蝌蚪爆料爆料线索投稿入口"
        description="向蝌蚪爆料投稿线索、纠正内容或咨询合作。写清时间地点与来源，编辑会核对后在回复工作日内答复你。"
        keywords="蝌蚪爆料投稿,蝌蚪爆料联系,蝌蚪爆料线索,蝌蚪爆料合作,蝌蚪爆料反馈"
        path="/contact"
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-8 md:py-16">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系投稿" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
            联系与投稿
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
            你手上的一条线索，可能就是下一条快讯的起点。无论是现场目击、内幕补充，还是内容纠错与合作咨询，
            都可以通过下面的方式告诉我们。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              联系方式
            </h2>
            <div className="mt-5 space-y-4">
              {channels.map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{item.label}</p>
                    <p className="mt-1 break-all font-heading text-sm text-primary-700">{item.value}</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{item.note}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              投稿须知
            </h2>
            <ol className="mt-5 space-y-4">
              {guideSteps.map((step) => (
                <li key={step.no} className="flex gap-4">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 font-heading text-xs font-bold text-background-50">
                    {step.no}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">{step.title}</span>
                    <span className="mt-1 block text-sm leading-relaxed text-foreground-600">
                      {step.desc}
                    </span>
                  </span>
                </li>
              ))}
            </ol>

            <p className="mt-8 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-xs leading-relaxed text-accent-900">
              提示：我们不接受无法核实的针对具体个人的负面指控，也不会公开投稿者的个人信息。
            </p>
          </div>

          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}